import { type Component, defineComponent, h, inject, provide, type Slots, type VNode } from 'vue'
import Block from './Block.vue'
import type { BlockProps } from './types'

export type BlockDecorator = Component<BlockProps>

type ChildRenderFunction = (injectedProps?: Partial<BlockProps>) => VNode

const decoratorsKey = Symbol('block-decorators')

export const BlockDecorated = defineComponent<BlockProps>({
    name: 'BlockDecorated',
    setup(props, { attrs, slots }) {
        const decorators = inject<BlockDecorator[]>(decoratorsKey, [])

        // Build the decorator chain ONCE
        const renderChain: ChildRenderFunction = buildRenderChain(decorators, props, attrs, slots)

        return () => renderChain()
    },
})

function buildRenderChain(
    decorators: BlockDecorator[],
    props: BlockProps,
    attrs: Record<string, unknown>,
    slots: Slots,
): ChildRenderFunction {
    let child: ChildRenderFunction = (injectedProps) =>
        h(Block, { ...props, ...attrs, ...injectedProps }, slots)

    for (let i = decorators.length - 1; i >= 0; i--) {
        const Decorator = decorators[i]
        const previousChild = child
        child = (injectedProps) =>
            h(Decorator, { ...props, ...attrs }, {
                default: (newInjectedProps?: Partial<BlockProps>) => previousChild({ ...injectedProps, ...newInjectedProps }),
            })
    }

    return child
}

export function useBlockDecorators(...decorators: BlockDecorator[]) {
    provide(decoratorsKey, [...decorators])
}
